<script setup lang="ts">

import { ElAlert, ElCard } from 'element-plus';
import { storeToRefs } from 'pinia';
import PersonalDataForm from '@/user/components/PersonalDataForm.vue';
import PersonalDataFormSkeleton from '@/user/components/PersonalDataFormSkeleton.vue';
import Heading from '@/application/components/Heading.vue';
import { useBaseDataStore } from '@/user/store/baseDataStore';
import { useUserDataStore } from '@/user/store/userDataStore';
import { useContractStore } from '@/contracts/stores/contractStore';
import PageContentWrapper from '@/application/layouts/ContentWrapper/PageContentWrapper.vue';
import { IonContent, IonRefresher, IonRefresherContent, type RefresherEventDetail } from '@ionic/vue';

const { isCompany } = storeToRefs(useBaseDataStore());
const { duzen } = useBaseDataStore();
const { userData, userDataLoading, userDataError } = storeToRefs(useUserDataStore());
const { fetchContractsAndDepots } = useContractStore();
const { fetchUserContacts, getBankAccounts } = useUserDataStore();

const errorMessage = `Es gab einen Fehler. Bitte ${duzen('lade', 'laden Sie')} die Seite neu oder
${duzen('probiere', 'probieren Sie')} es zu einem späteren Zeitpunkt nochmal.`;

async function fetchData(): Promise<void> {
  await Promise.all([
    fetchUserContacts(),
    getBankAccounts(),
    fetchContractsAndDepots(),
  ]);
}

fetchData();

async function handleRefresh(event: CustomEvent<RefresherEventDetail>): Promise<void> {
  try {
    await fetchData();
  } finally {
    event.detail.complete();
  }
}
</script>

<template>
  <IonContent class="ion-no-padding">
    <IonRefresher slot="fixed" @ion-refresh="handleRefresh">
      <IonRefresherContent />
    </IonRefresher>

    <PageContentWrapper>
      <div class="flex flex-col md:w-10/12 lg:w-8/12 gap-2 -mx-4 md:mx-auto">
        <h1 class="hidden md:block text-center">
          {{ isCompany ? 'Unsere Daten' : 'Meine Daten' }}
        </h1>

        <span class="md:hidden mx-4 font-semibold text-lg">Kontaktinformationen</span>
        <ElCard class="mb-10">
          <Heading
            level="5"
            text="Kontaktinformationen"
            :icon="{name: 'user-circle', variant: 'regular', class: 'text-secondary mr-2'}"
            class="hidden md:block border-b border-gray-200 pb-2 mb-6 font-semibold"
          />
          <PersonalDataFormSkeleton v-if="userDataLoading" />
          <PersonalDataForm v-else-if="userData" />
          <ElAlert
            v-else-if="userDataError"
            class="my-10"
            type="error"
            title="Es ist leider ein Fehler aufgetreten."
            show-icon
          >
            {{ errorMessage }}
          </ElAlert>
        </ElCard>
      </div>
    </PageContentWrapper>
  </IonContent>
</template>
